/**
 * Topbar (Project Harmony) — 5 primary destinations. Nothing more.
 *
 * Amendment 12 (Task-First Navigation): names answer "what am I trying
 * to accomplish?" — never "which subsystem performs this work?".
 *
 * Progressive Disclosure (Amendment 11): the nav destinations are stable,
 * but the AmbientAutopilotPill and inner-page tabs unlock as milestones
 * are reached. See ``useWorkspaceMilestones``.
 */
import { Link, useLocation } from "react-router-dom";
import {
  BarChart3, Brain, CreditCard, Folder, Home as HomeIcon, Images,
  LogOut, Megaphone, Menu, Palette, Pencil, Rocket, Settings2, Sparkles, Wand2,
} from "lucide-react";
import { Fragment, useEffect, useState } from "react";

import { logout as apiLogout } from "@/entities/auth";
import { useAuthStore } from "@/entities/auth/store";
import { WorkspaceSwitcher } from "@/features/workspace-switcher";
import { setAuthToken } from "@/shared/api/http";
import { OrkexraLogo } from "@/shared/ui/OrkexraLogo";
import { CommandPalette } from "@/widgets/CommandPalette/CommandPalette";
import { AmbientAutopilotPill } from "@/widgets/AmbientAutopilot/AmbientAutopilotPill";

// Patch 36.0 — Project Obsidian nav refinement. Task-oriented labels
// only; no internal implementation language on the primary nav rail.
// URLs are intentionally unchanged so bookmarks and deep links stay
// stable.
//
// Patch 37.4.4 — Split the Brain nav item. Prior to this patch a
// single "Business Brain" pill routed to ``/brand-brain`` (visible
// label was Business, destination was Brand). Founders reported they
// could not reach Brand Brain OR the Full Brain Editor from the
// menu; splitting matches the two distinct missions.
const NAV = [
  { to: "/",              label: "Executive Home", icon: HomeIcon,   testId: "nav-home",
    match: (p: string) => p === "/" || p.startsWith("/home") },
  { to: "/business-brain", label: "Business Brain", icon: Brain,     testId: "nav-business-brain",
    match: (p: string) => p.startsWith("/business-brain") },
  { to: "/brand-brain",    label: "Brand Brain",    icon: Palette,   testId: "nav-brand-brain",
    match: (p: string) => p.startsWith("/brand-brain") },
  { to: "/campaigns",     label: "Campaigns",      icon: Megaphone,  testId: "nav-campaigns",
    match: (p: string) => p.startsWith("/campaigns") || p === "/review" || p === "/content-review" || p.startsWith("/campaign-commander") || p === "/ops" || p === "/mission-control" },
  { to: "/ai-studio",     label: "AI Studio",      icon: Wand2,      testId: "nav-ai-studio",
    match: (p: string) => p === "/ai-studio" || p === "/studio" },
  { to: "/media",         label: "Media Engine",   icon: Images,     testId: "nav-media",
    match: (p: string) => p.startsWith("/media") },
  { to: "/vault",         label: "Vault",          icon: Folder,     testId: "nav-vault",
    match: (p: string) => p.startsWith("/vault") || p === "/library" || p.startsWith("/assets") },
  { to: "/publishing",    label: "Publishing",     icon: Rocket,     testId: "nav-publishing",
    match: (p: string) => p.startsWith("/publishing") },
  { to: "/intelligence",  label: "Intelligence",   icon: BarChart3,  testId: "nav-intelligence",
    match: (p: string) => p.startsWith("/intelligence") },
  { to: "/workspace/autopilot", label: "Autopilot", icon: Sparkles, testId: "nav-autopilot",
    match: (p: string) => p.startsWith("/workspace/autopilot") },
  { to: "/workspace",     label: "Settings",       icon: Settings2,  testId: "nav-workspace",
    match: (p: string) => (p.startsWith("/workspace") && !p.startsWith("/workspace/autopilot")) || p.startsWith("/settings") || p.startsWith("/billing") || p === "/health" || p === "/history" || p === "/academy" || p === "/launch-readiness" },
] as const;

export function Topbar(): JSX.Element {
  const loc = useLocation();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const [palette, setPalette] = useState(false);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const doLogout = async () => {
    try { await apiLogout(); } catch { /* noop */ }
    clear();
    setAuthToken(null);
    // Use a hard replacement after clearing all persisted auth state. This
    // prevents route guards or stale hydration from bouncing the user back
    // into the authenticated shell during logout.
    window.location.replace("/login?loggedOut=1");
  };


  return (
    <>
      <header
        data-testid="app-topbar"
        className="sticky top-0 z-30 border-b border-[color:var(--md-line)] backdrop-blur-xl"
        style={{ background: "linear-gradient(180deg, rgba(20,24,33,0.7), rgba(15,18,24,0.45))" }}
      >
        <div className="mx-auto flex max-w-[1400px] items-center gap-2 px-3 py-3 md:gap-3 md:px-6">
          <button
            onClick={() => setDrawer((v) => !v)}
            className="shrink-0 rounded-md p-2 text-[var(--md-fog)] hover:bg-[var(--md-ink-2)] md:hidden"
            aria-label="Open menu"
            data-testid="topbar-menu"
          >
            <Menu className="h-4 w-4" />
          </button>

          <Link to="/" className="flex shrink-0 items-center gap-3" data-testid="topbar-logo">
            {/* Patch 36.1 — Orkexra compact wordmark. The workspace
                name lives on the WorkspaceSwitcher pill immediately
                to the right, so we intentionally do NOT duplicate it
                inside the logo slot. This keeps the topbar's
                horizontal budget stable at every desktop width. */}
            <OrkexraLogo variant="wordmark" size={28} className="hidden sm:inline-flex" />
            <OrkexraLogo variant="mark" size={28} className="inline-flex sm:hidden" />
          </Link>

          <span aria-hidden className="hidden h-6 w-px bg-[color:var(--md-line)] md:block" />
          <WorkspaceSwitcher />

          {/* Primary nav — task-oriented labels (Patch 36.0). If the
              nav's intrinsic width ever exceeds the flex slot at very
              narrow desktops, ``overflow-x-auto`` keeps every pill
              inside the nav rect AND reachable via horizontal scroll —
              pills never render behind sibling chrome. */}
          <nav
            className="hidden min-w-0 flex-1 items-center justify-start gap-0.5 overflow-x-auto md:flex [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="topbar-primary-nav"
          >
            {NAV.map((n) => (
              <Fragment key={n.to}>
                <NavPill
                  to={n.to}
                  icon={<n.icon className="h-3.5 w-3.5" aria-hidden />}
                  active={n.match(loc.pathname)}
                  testId={n.testId}
                >
                  {n.label}
                </NavPill>
                {n.to === "/business-brain" || n.to === "/brand-brain" ? (
                  <Link
                    to={`${n.to}/advanced`}
                    data-testid={`${n.testId}-full-editor`}
                    aria-label={`Edit all ${n.label} answers`}
                    title={`Edit all ${n.label} answers`}
                    className="inline-flex shrink-0 rounded-md p-1.5 text-[var(--md-fog)] transition hover:bg-[var(--md-ink-2)] hover:text-[var(--md-ivory-0)]"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                  </Link>
                ) : null}
              </Fragment>
            ))}
          </nav>

          <div className="flex-1 md:hidden" />

          {/* Right-cluster chrome permanently retired on desktop
              (Patch 36.0 · Project Obsidian, "every pixel must earn
              its place"). Autopilot state now lives on the Executive
              Home Business Pulse. The Plans link is reachable from the
              footer. ⌘K still opens the palette via the keyboard
              shortcut — no visible button needed. */}

          {user ? (
            <button
              data-testid="topbar-logout"
              onClick={doLogout}
              title={`Signed in as ${user.email}`}
              className="grid h-8 w-8 place-items-center rounded-full border border-[color:var(--md-line)] text-[var(--md-fog)] transition hover:border-[var(--md-amber)] hover:text-[var(--md-ivory-0)]"
              style={{ background: "linear-gradient(135deg, var(--ws-primary), var(--ws-accent))", opacity: 0.85 }}
            >
              <LogOut className="h-3.5 w-3.5 text-[var(--md-ink-0)]" />
            </button>
          ) : (
            <Link
              data-testid="topbar-signin"
              to="/login"
              className="md-btn md-btn--primary"
            >
              Sign in
            </Link>
          )}
        </div>
      </header>

      {/* Mobile drawer — 5 items, thumb-reachable. */}
      {drawer ? (
        <div
          className="fixed inset-0 z-40 bg-[var(--md-ink-0)]/80 backdrop-blur-md md:hidden"
          onClick={() => setDrawer(false)}
          data-testid="mobile-drawer"
        >
          <nav
            onClick={(e) => e.stopPropagation()}
            className="ml-auto flex h-full w-[280px] flex-col gap-1 border-l border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-4"
          >
            <div className="mb-4 md-eyebrow">Navigate</div>
            {NAV.map((n) => (
              <div key={n.to} className="flex flex-col">
                <Link
                  to={n.to}
                  onClick={() => setDrawer(false)}
                  data-testid={`mobile-${n.testId}`}
                  className="flex items-center gap-3 rounded-md px-3 py-3 text-sm text-[var(--md-ivory-0)] hover:bg-[var(--md-ink-2)]"
                >
                  <n.icon className="h-4 w-4 text-[var(--md-fog)]" aria-hidden />
                  <span>{n.label}</span>
                </Link>
                {/* Patch 37.4.4 — direct sidebar entry into the Full
                    Brain Editor. Available for both brains so
                    founders can jump straight into structured
                    multi-field edit without stepping through the
                    guided Review each time. */}
                {n.to === "/business-brain" || n.to === "/brand-brain" ? (
                  <Link
                    to={`${n.to}/advanced`}
                    onClick={() => setDrawer(false)}
                    data-testid={`mobile-${n.testId}-full-editor`}
                    className="ml-10 flex items-center gap-2 rounded-md px-3 py-2 text-xs text-[var(--md-fog)] hover:bg-[var(--md-ink-2)] hover:text-[var(--md-ivory-0)]"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                    <span>Full Brain Editor · Edit all answers</span>
                  </Link>
                ) : null}
              </div>
            ))}
            <Link
              to="/pricing"
              onClick={() => setDrawer(false)}
              data-testid="mobile-nav-pricing"
              className="flex items-center gap-3 rounded-md px-3 py-3 text-sm text-[var(--md-ivory-0)] hover:bg-[var(--md-ink-2)]"
            >
              <CreditCard className="h-4 w-4 text-[var(--md-fog)]" aria-hidden />
              <span>Plans &amp; Pricing</span>
            </Link>
            <div className="mt-6 border-t border-[color:var(--md-line)] pt-4">
              <div className="mb-2 md-eyebrow">Marketing Department</div>
              <AmbientAutopilotPill />
            </div>
          </nav>
        </div>
      ) : null}

      <CommandPalette open={palette} onClose={() => setPalette(false)} />
    </>
  );
}

function NavPill({
  to, icon, active, testId, children,
}: {
  to: string;
  icon: React.ReactNode;
  active: boolean;
  testId: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 py-1.5 text-[13px] font-medium transition ${
        active
          ? "bg-[var(--md-ink-2)] text-[var(--md-ivory-0)]"
          : "text-[var(--md-fog)] hover:text-[var(--md-ivory-0)]"
      }`}
      style={{
        fontFamily: "var(--font-operational, Inter, system-ui, sans-serif)",
        boxShadow: active ? "inset 0 -1px 0 var(--md-amber)" : undefined,
      }}
    >
      {icon}
      {children}
    </Link>
  );
}
